import { Skeleton } from "@fullscript/aviary-web";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { HeadingNode } from "@lexical/rich-text";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { useHydrated } from "@shared/hooks/useHydrated/useHydrated";
import { isDevelopment, isSSR } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { useToast } from "../Toast";

import { LexicalEditor } from "./LexicalEditor";
import { ToolbarButton } from "./Toolbar";

import * as styles from "./RichTextEditor.styles";

interface Props {
  /**
   * Initial html to be displayed in the editor
   */
  initialInput?: string;
  /**
   * Callback from the editor on change
   */
  onChange?: (htmlMessage: string) => void;
  /**
   * Place additional information on the right hand side of the toolbar
   */
  toolbarInfo?: ReactNode;
  /**
   *  Override default placeholder text
   */
  placeholder?: string;
  /**
   * disable editable mode
   */
  readOnly?: boolean;
  /**
   * When true, the editor will not autoFocus
   */
  hideAutoFocus?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/inputs/RichTextEditor
 */
const RichTextEditor = ({
  initialInput,
  onChange,
  toolbarInfo,
  placeholder,
  readOnly,
  hideAutoFocus,
}: Props) => {
  const { makeToast } = useToast();
  const { t } = useTranslation();
  const isHydrated = useHydrated();

  const editorConfig: InitialConfigType = {
    theme: styles.RichTextEditorTheme,
    onError: error => {
      if (isDevelopment()) {
        makeToast("error", `${error?.name}: ${error?.message}`);
      } else {
        makeToast("error", t(l.aviary.richTextEditor.error));
      }
    },
    namespace: "RTE",
    nodes: [ListNode, ListItemNode, HeadingNode, LinkNode],
    editable: !readOnly,
  };

  if (isSSR() && !isHydrated) {
    return <Skeleton variant="typography" typographyType="h3" isFullWidth />;
  }

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <LexicalEditor
        initialInput={initialInput}
        onChange={onChange}
        toolbarInfo={toolbarInfo}
        placeholder={placeholder}
        readOnly={readOnly}
        hideAutoFocus={hideAutoFocus}
      />
    </LexicalComposer>
  );
};

RichTextEditor.ToolbarButton = ToolbarButton;

export { RichTextEditor };
